<template>
  <div class="sso-page">
    <div class="top-bg"></div>
    <div class="overlay"></div>

    <div class="sso-card">
      <div class="card-head">
        <div class="eyebrow">OAuth2 SSO</div>
        <div class="title">统一登录授权</div>
        <div class="subtitle">{{ client.name || '第三方应用' }} 正在请求访问你的账号权限</div>
      </div>

      <common-loading v-if="pageLoading" text="正在准备授权信息" />

      <div v-else-if="errorMessage" class="state-box">
        <div class="state-title">授权请求不可用</div>
        <div class="state-text">{{ errorMessage }}</div>
        <el-button class="ghost-btn" @click="$router.replace('/')">返回首页</el-button>
      </div>

      <el-form v-else class="auth-form" @submit.native.prevent>
        <div class="scope-title">授权范围</div>
        <div class="scope-list">
          <label v-for="scope in scopeOptions" :key="scope.key" class="scope-item">
            <input v-model="form.scopes" type="checkbox" :value="scope.key" />
            <span class="checkbox-mark"></span>
            <span class="scope-text">
              <span class="scope-name">{{ formatScope(scope.key) }}</span>
              <span class="scope-key">{{ scope.key }}</span>
            </span>
          </label>
        </div>

        <div class="actions">
          <el-button class="agree-btn" type="primary" :loading="formLoading" @click="handleAuthorize(true)"> 同意授权 </el-button>
          <el-button class="reject-btn" :disabled="formLoading" @click="handleAuthorize(false)"> 拒绝 </el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import CommonLoading from '@/components/CommonLoading';
import { authorize, getAuthorize } from '@/api/oauth2';

export default {
  name: 'SSOLogin',
  components: {
    CommonLoading
  },
  data() {
    return {
      client: {
        name: '',
        logo: ''
      },
      queryParams: {
        responseType: '',
        clientId: '',
        redirectUri: '',
        state: '',
        scopes: [],
        auto: false
      },
      scopeOptions: [],
      form: {
        scopes: []
      },
      pageLoading: true,
      formLoading: false,
      errorMessage: ''
    };
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.pageLoading = true;
      this.errorMessage = '';
      try {
        this.readQuery();
        if (!this.queryParams.clientId || !this.queryParams.redirectUri || !this.queryParams.responseType) {
          this.errorMessage = '缺少 client_id、redirect_uri 或 response_type 参数。';
          return;
        }

        const data = await getAuthorize(this.queryParams.clientId);
        this.client = (data && data.client) || this.client;
        this.setupScopes((data && data.scopes) || []);

        if (this.queryParams.auto) {
          await this.handleAutoAuthorize();
        }
      } catch (error) {
        this.errorMessage = error.message || '授权信息加载失败，请稍后重试。';
      } finally {
        this.pageLoading = false;
      }
    },
    readQuery() {
      const query = this.$route.query || {};
      this.queryParams.responseType = query.response_type || '';
      this.queryParams.clientId = query.client_id || '';
      this.queryParams.redirectUri = query.redirect_uri || '';
      this.queryParams.state = query.state || '';
      this.queryParams.scopes = query.scope ? String(query.scope).split(' ').filter(Boolean) : [];
      this.queryParams.auto = String(query.auto || '').toLowerCase() === 'true';
    },
    setupScopes(scopes) {
      let options = scopes;
      if (this.queryParams.scopes.length > 0) {
        options = scopes.filter(scope => this.queryParams.scopes.indexOf(scope.key) >= 0);
      } else {
        this.queryParams.scopes = scopes.map(scope => scope.key);
      }
      this.scopeOptions = options;
      this.form.scopes = options.filter(scope => scope.value).map(scope => scope.key);
    },
    async handleAutoAuthorize() {
      const checkedScopes = this.form.scopes.length > 0 ? this.form.scopes : this.queryParams.scopes;
      const uncheckedScopes = this.queryParams.scopes.filter(scope => checkedScopes.indexOf(scope) === -1);
      const redirectUrl = await this.doAuthorize(true, checkedScopes, uncheckedScopes);
      console.log('Auto authorize redirectUrl:', redirectUrl);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    },
    async handleAuthorize(approved) {
      if (this.formLoading) {
        return;
      }
      const checkedScopes = approved ? this.form.scopes : [];
      const uncheckedScopes = approved ? this.queryParams.scopes.filter(scope => checkedScopes.indexOf(scope) === -1) : this.queryParams.scopes;

      this.formLoading = true;
      try {
        const redirectUrl = await this.doAuthorize(false, checkedScopes, uncheckedScopes);
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        this.errorMessage = error.message || '授权失败，请稍后重试。';
      } finally {
        this.formLoading = false;
      }
    },
    doAuthorize(autoApprove, checkedScopes, uncheckedScopes) {
      return authorize({
        responseType: this.queryParams.responseType,
        clientId: this.queryParams.clientId,
        redirectUri: this.queryParams.redirectUri,
        state: this.queryParams.state,
        autoApprove,
        checkedScopes,
        uncheckedScopes
      });
    },
    formatScope(scope) {
      switch (scope) {
        case 'user.read':
          return '访问你的个人信息';
        case 'user.write':
          return '修改你的个人信息';
        default:
          return scope;
      }
    }
  }
};
</script>

<style scoped>
.sso-page {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  min-height: 100vh;
  padding: 24px;
  overflow-x: hidden;
  overflow-y: auto;
  background: #070b12;
}

.top-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(7, 11, 18, 0.2), rgba(7, 11, 18, 0.92)), url('../case/img/case-top-bg.png') no-repeat 50% 0;
  background-size: cover;
  transform: scale(1.03);
}

.overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(8, 20, 36, 0.7), rgba(6, 16, 31, 0.5) 46%, rgba(3, 8, 17, 0.88));
}

.sso-card {
  position: relative;
  z-index: 2;
  --loading-bg: #111927;
  width: 460px;
  max-width: calc(100vw - 32px);
  margin: auto 0;
  padding: 36px 32px 30px;
  color: #fff;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.05)), rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  box-shadow: 0 20px 52px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(18px) saturate(135%);
  -webkit-backdrop-filter: blur(18px) saturate(135%);
}

.eyebrow {
  color: rgba(255, 255, 255, 0.58);
  font-size: 12px;
  letter-spacing: 0;
}

.title {
  margin-top: 8px;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: 0;
}

.subtitle {
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 14px;
  line-height: 1.7;
}

.auth-form {
  margin-top: 28px;
}

.scope-title {
  color: rgba(255, 255, 255, 0.78);
  font-size: 14px;
}

.scope-list {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.scope-item {
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 12px;
  align-items: center;
  min-height: 58px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.25s ease, background-color 0.25s ease, transform 0.25s ease;
}

.scope-item:hover {
  background: rgba(255, 255, 255, 0.11);
  border-color: rgba(255, 255, 255, 0.28);
  transform: translateY(-1px);
}

.scope-item input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkbox-mark {
  width: 18px;
  height: 18px;
  border: 1px solid rgba(255, 255, 255, 0.48);
  border-radius: 5px;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.scope-item input:checked + .checkbox-mark {
  background: #008dff;
  border-color: #008dff;
  box-shadow: 0 0 0 3px rgba(0, 141, 255, 0.18);
}

.scope-item input:checked + .checkbox-mark::after {
  content: '';
  display: block;
  width: 8px;
  height: 4px;
  margin: 5px 0 0 4px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg);
}

.scope-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.scope-name {
  font-size: 15px;
  color: #fff;
}

.scope-key {
  overflow-wrap: anywhere;
  color: rgba(255, 255, 255, 0.46);
  font-size: 12px;
}

.actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px;
  gap: 12px;
  margin-top: 28px;
}

.agree-btn,
.reject-btn,
.ghost-btn {
  height: 48px;
  border-radius: 8px;
  font-size: 15px;
}

.agree-btn {
  border: 0;
  background: rgba(255, 255, 255, 0.9);
  color: #0d2542;
  font-weight: 600;
}

.reject-btn,
.ghost-btn {
  color: rgba(255, 255, 255, 0.86);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.18);
}

.state-box {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 28px;
  text-align: center;
}

.state-title {
  font-size: 20px;
}

.state-text {
  margin: 12px 0 22px;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.7;
}

@media (max-width: 480px) {
  .sso-page {
    padding: 14px;
  }

  .sso-card {
    padding: 28px 18px 22px;
    border-radius: 10px;
  }

  .title {
    font-size: 24px;
  }

  .actions {
    grid-template-columns: 1fr;
  }
}
</style>
